import React, { useState, useEffect, useRef } from 'react';
import { ChefHat, Package, BookOpen, Loader2, X } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
    Drawer, DrawerContent, DrawerTitle, DrawerDescription
} from '@/components/ui/drawer';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { toast } from 'sonner';
import { recipesAPI } from '@/lib/api';
import { formatCurrency, pluralUnit } from './recipeUtils';
import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';

export default function RecipeDetailDialog({ open, onClose, recipe }) {
    const { hasPermission, user } = useAuth();
    const isMobile = useIsMobile();
    const [recipeStack, setRecipeStack] = useState([]);
    const [loading, setLoading] = useState(false);
    const metricsRef = useRef(null);
    const [metricsHeight, setMetricsHeight] = useState(0);
    const current = recipeStack[recipeStack.length - 1] || recipe;

    useEffect(() => {
        if (open && recipe) {
            setRecipeStack([recipe]);
            window.history.pushState({ recipeDialog: true }, '');
        } else if (!open) {
            setRecipeStack([]);
        }
    }, [open, recipe]);

    // History API: push state when entering sub-recipe, handle back button
    useEffect(() => {
        if (!open) return;
        const handlePopState = () => {
            if (recipeStack.length > 1) {
                setRecipeStack(prev => prev.slice(0, -1));
            } else {
                onClose(false);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [open, recipeStack.length, onClose]);

    useEffect(() => {
        if (metricsRef.current) {
            setMetricsHeight(metricsRef.current.offsetHeight);
        }
    }, [current]);

    const openSubRecipe = async (subRecipeId) => {
        try {
            setLoading(true);
            const res = await recipesAPI.getById(subRecipeId);
            window.history.pushState({ subRecipe: true }, '');
            setRecipeStack(prev => [...prev, res.data]);
        } catch {
            toast.error('Erreur lors du chargement de la sous-recette');
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => {
        window.history.back();
    };

    if (!recipe) return null;

    const totalCost = current?.total_cost || 0;
    const costPerPortion = current?.cost_per_portion || 0;
    const canViewPrices = hasPermission('prices_view') && user?.role !== 'operator';

    const handleOpenChange = (isOpen) => {
        if (!isOpen) window.history.back();
    };

    // Le TITRE du drawer/dialog (identique sur mobile & desktop)
    const titleNode = (
        <span className="flex items-center gap-2 pr-10">
            {recipeStack.length > 1 && (
                <button
                    onClick={goBack}
                    className="p-1 rounded-md hover:bg-slate-100 transition-colors"
                    data-testid="detail-back-btn"
                    aria-label="Retour"
                >
                    <X className="w-4 h-4 text-slate-500" style={{ transform: 'rotate(45deg)' }} />
                </button>
            )}
            <ChefHat className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">{current?.name}</span>
        </span>
    );

    const descriptionNode = (
        <>
            {recipeStack.length > 1 && (
                <span className="flex items-center gap-1 text-xs text-slate-400 mb-1 flex-wrap">
                    {recipeStack.map((r, i) => (
                        <span key={i} className="flex items-center gap-1">
                            {i > 0 && <span>/</span>}
                            <button
                                onClick={() => i < recipeStack.length - 1 && setRecipeStack(prev => prev.slice(0, i + 1))}
                                className={i < recipeStack.length - 1 ? 'hover:underline cursor-pointer text-slate-500' : 'font-medium text-slate-700'}
                            >
                                {r.name}
                            </button>
                        </span>
                    ))}
                </span>
            )}
            {current?.category && <span className="inline-block bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full mr-2">{current.category}</span>}
            {current?.description || 'Aucune description'}
        </>
    );

    // === Version MOBILE : Drawer Vaul (bottom-sheet) ===
    // Vaul positionne le sheet via `bottom: 0` + `translateY`, ce qui evite le bug
    // Android Chrome ou `position: fixed top: 0` combine a `overflow: hidden` sur body
    // se retrouve decale de la scrollY memorisee.
    if (isMobile) {
        return (
            <Drawer open={open} onOpenChange={handleOpenChange} shouldScaleBackground={false}>
                <DrawerContent
                    className="p-0 gap-0 flex flex-col h-[100dvh] max-h-[100dvh] overflow-hidden bg-white"
                    data-testid="recipe-detail-dialog"
                    style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
                >
                    {/* Header sticky : reste visible pendant le scroll, abrite la croix de fermeture */}
                    <div className="flex-shrink-0 relative px-4 pt-2 pb-3 pr-16 border-b border-slate-200 bg-white">
                        <DrawerTitle asChild>
                            <div className="text-lg font-semibold leading-tight">{titleNode}</div>
                        </DrawerTitle>
                        <DrawerDescription asChild>
                            <div className="text-sm text-slate-500 mt-1">{descriptionNode}</div>
                        </DrawerDescription>
                        <button
                            onClick={() => handleOpenChange(false)}
                            className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-slate-900/85 flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                            aria-label="Fermer"
                            data-testid="drawer-close-btn"
                        >
                            <X className="w-5 h-5 text-white" />
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex-1 flex items-center justify-center py-12 min-h-0">
                            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                    ) : (
                        <div
                            className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-4"
                            style={{ paddingBottom: 'max(0.75rem, calc(env(safe-area-inset-bottom, 0px) + 0.75rem))' }}
                        >
                            {renderRecipeBody({ current, totalCost, costPerPortion, openSubRecipe, metricsRef, metricsHeight, canViewPrices })}
                        </div>
                    )}
                </DrawerContent>
            </Drawer>
        );
    }

    // === Version DESKTOP : Dialog Radix classique ===
    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="p-0 sm:p-0 gap-0 flex flex-col sm:h-auto sm:max-h-[85vh] sm:max-w-3xl overflow-hidden"
                data-testid="recipe-detail-dialog"
            >
                <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 pr-16 border-b border-slate-200 bg-white">
                    <DialogTitle asChild>{titleNode}</DialogTitle>
                    <DialogDescription asChild><div>{descriptionNode}</div></DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center py-12 min-h-0">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
                        {renderRecipeBody({ current, totalCost, costPerPortion, openSubRecipe, metricsRef, metricsHeight, canViewPrices })}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

// Body content shared between Drawer (mobile) and Dialog (desktop)
function renderRecipeBody({ current, totalCost, costPerPortion, openSubRecipe, metricsRef, metricsHeight, canViewPrices }) {
    return (
        <>
            {/* Photo + Summary side by side */}
            <div className="flex gap-3 items-start">
                {current?.image_url && (
                    <div className="rounded-lg overflow-hidden border border-slate-200 flex-shrink-0"
                        style={{ height: metricsHeight || 'auto', width: metricsHeight ? (metricsHeight * 4 / 3) : 'auto', aspectRatio: metricsHeight ? undefined : '4/3' }}>
                        <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${current.image_url}`}
                            alt={current.name}
                            className="w-full h-full object-cover"
                            data-testid="detail-recipe-image"
                        />
                    </div>
                )}
                <div className="flex flex-col gap-1.5 w-36 flex-shrink-0" ref={metricsRef}>
                    <div className="bg-slate-50 rounded-lg px-2 py-1.5 text-center">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Rendement</p>
                        <p className="text-sm font-bold text-slate-900" data-testid="detail-portions">{current?.portions} {pluralUnit(current?.yield_unit || 'portion', current?.portions)}</p>
                    </div>
                    {canViewPrices && (
                        <div className="bg-slate-50 rounded-lg px-2 py-1.5 text-center">
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Coût total</p>
                            <p className="text-sm font-bold text-slate-900" data-testid="detail-total-cost">{formatCurrency(totalCost)}</p>
                        </div>
                    )}
                    {canViewPrices && (
                        <div className="bg-orange-50 rounded-lg px-2 py-1.5 text-center border border-orange-100">
                            <p className="text-[10px] text-orange-600 uppercase tracking-wider">Coût / {pluralUnit(current?.yield_unit || 'portion', 1)}</p>
                            <p className="text-sm font-bold text-orange-600" data-testid="detail-cost-per-portion">{formatCurrency(costPerPortion)}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Allergènes */}
            {current?.allergens && current.allergens.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-medium text-orange-600 uppercase tracking-wider">Allergènes :</span>
                    {current.allergens.map((a) => (
                        <span key={a} className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded-full border border-orange-200">
                            {a}
                        </span>
                    ))}
                </div>
            )}

            {/* Ingredients Table */}
            <div>
                <h3 className="font-semibold text-slate-900 mb-2">Ingrédients ({current?.ingredients?.length || 0})</h3>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50">
                                <TableHead className="text-xs uppercase min-w-[200px]">Ingrédient</TableHead>
                                <TableHead className="text-xs uppercase text-right">Quantité</TableHead>
                                <TableHead className="text-xs uppercase">Unité</TableHead>
                                {canViewPrices && <TableHead className="text-xs uppercase text-right">P.U.</TableHead>}
                                {canViewPrices && <TableHead className="text-xs uppercase text-right">Coût</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(current?.ingredients || []).map((ing, i) => (
                                <TableRow key={i} data-testid={`detail-ingredient-${i}`}>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            {ing.type === 'product' ? (
                                                ing.industrial_product_image ? (
                                                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200 bg-white">
                                                        <img src={`${process.env.REACT_APP_BACKEND_URL}${ing.industrial_product_image}`} alt="" className="w-full h-full object-contain" />
                                                    </div>
                                                ) : (
                                                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-50">
                                                        <Package className="w-5 h-5 text-slate-400" />
                                                    </div>
                                                )
                                            ) : (
                                                ing.sub_recipe_image ? (
                                                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-orange-200 bg-white">
                                                        <img src={`${process.env.REACT_APP_BACKEND_URL}${ing.sub_recipe_image}`} alt="" className="w-full h-full object-contain" />
                                                    </div>
                                                ) : (
                                                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-orange-50">
                                                        <BookOpen className="w-5 h-5 text-orange-400" />
                                                    </div>
                                                )
                                            )}
                                            <div>
                                                {ing.type === 'recipe' && ing.sub_recipe_id ? (
                                                    <button
                                                        onClick={() => openSubRecipe(ing.sub_recipe_id)}
                                                        className="font-medium text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                                                        data-testid={`sub-recipe-link-${i}`}
                                                    >
                                                        {ing.sub_recipe_name || 'Sous-recette inconnue'}
                                                    </button>
                                                ) : (
                                                    <p className="font-medium text-sm">
                                                        {ing.type === 'product'
                                                            ? ing.industrial_product_name || 'Produit inconnu'
                                                            : ing.sub_recipe_name || 'Sous-recette inconnue'}
                                                    </p>
                                                )}
                                                {ing.type === 'product' && ing.supplier_name && (
                                                    <p className="text-xs text-slate-400">{ing.supplier_name}</p>
                                                )}
                                                {ing.ingredient_allergens && ing.ingredient_allergens.length > 0 && (
                                                    <div className="flex items-center gap-0.5 flex-wrap mt-0.5">
                                                        {ing.ingredient_allergens.map((a) => (
                                                            <span key={a} className="px-1 py-0 bg-orange-100 text-orange-600 text-[9px] font-medium rounded">
                                                                {a}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right text-sm">{ing.quantity}</TableCell>
                                    <TableCell className="text-sm text-slate-500">{pluralUnit(ing.unit, ing.quantity) || '-'}</TableCell>
                                    {canViewPrices && <TableCell className="text-right text-sm text-slate-500">{ing.unit_cost ? formatCurrency(ing.unit_cost) : '-'}</TableCell>}
                                    {canViewPrices && <TableCell className="text-right text-sm font-medium">{ing.total_cost ? formatCurrency(ing.total_cost) : '-'}</TableCell>}
                                </TableRow>
                            ))}
                            {(!current?.ingredients || current.ingredients.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center text-slate-400 py-6">
                                        Aucun ingrédient
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    );
}
